import {
  boundCheckboxField,
  boundTextField,
  Css,
  FormPageLayout,
  MultiSelectField,
  StaticField,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Price } from "src/components";
import { ReadyPlanStatus, SaveProductOfferingConfigInput } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  createCheckoutStepSections,
  createOptionCostById,
  createOptionGroups,
  createSelectedOptionIdByGroupId,
  getActiveOptions,
  getArchivedOptions,
  getDisabledOptionIds,
  getMissingRequiredRpos,
  getOptionLabel,
  ReadyPlanOptionGroupSelect,
  sumComputeResultCostInCents,
  toFormReadyPlanOptions,
  updateSelectedOptionIdsForGroup,
} from "src/routes/components/ProductOfferingConfigUtils";
import {
  AddProductOfferingConfig_ComputeResultFragment,
  AddProductOfferingConfigMetaQuery,
  EditProductOfferingConfig_ProductOfferingConfigFragment,
  useAddProductOfferingConfig_ComputeLazyQuery,
  useAddProductOfferingConfig_ProgramDataLazyQuery,
  useAddProductOfferingConfigMetaQuery,
  useEditProductOfferingConfigQuery,
  useSaveProductOfferingConfigMutation,
} from "src/routes/libraries/product-offering-configs/AddProductOfferingConfigPage.generated";
import { ProductOfferingConfigParams } from "src/routes/routesDef";
import {
  createProductOfferingConfigsCompareUrl,
  createProductOfferingConfigsUrl,
  createProductOfferingUrl,
} from "src/RouteUrls";
import { isDefined, pluralize, queriesResult } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import { CreateProjectFromOfferingConfigModal } from "./CreateProjectFromOfferingConfigModal";

export function AddProductOfferingConfigPage() {
  const { productOfferingId, productOfferingConfigId } = useRouteParams<ProductOfferingConfigParams>();
  const configQuery = useEditProductOfferingConfigQuery({
    variables: { id: productOfferingConfigId },
    skip: !productOfferingConfigId,
  });
  const metaDataQuery = useAddProductOfferingConfigMetaQuery({
    variables: { productOfferingId },
    fetchPolicy: "no-cache",
    skip: isDefined(productOfferingConfigId) && configQuery.loading,
  });

  return queriesResult([metaDataQuery, configQuery] as const, {
    data: (metaData, configData) => (
      <AddProductOfferingConfigPageView productOfferingConfig={configData?.productOfferingConfig} metaData={metaData} />
    ),
  });
}

type AddOfferingConfigPageProps = {
  metaData: AddProductOfferingConfigMetaQuery;
  productOfferingConfig?: EditProductOfferingConfig_ProductOfferingConfigFragment;
};

export function AddProductOfferingConfigPageView(props: AddOfferingConfigPageProps) {
  const { productOfferingId } = useRouteParams<ProductOfferingConfigParams>();
  const [{ devId, configIds, projectId }] = useQueryParams({
    devId: StringParam,
    configIds: StringParam,
    projectId: StringParam,
  });
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { metaData, productOfferingConfig } = props;
  const isNew = !productOfferingConfig;
  const isArchivedOffering = metaData.productOffering?.status.code === ReadyPlanStatus.Archived;
  const initialOptionIds = useMemo(
    () => productOfferingConfig?.readyPlanOptions.filter((rpo) => rpo.active).map((rpo) => rpo.id) ?? [],
    [productOfferingConfig],
  );

  const [allOptions, archivedOptions] = useMemo(
    () => [getActiveOptions(metaData.productOffering.options), getArchivedOptions(metaData.productOffering.options)],
    [metaData.productOffering.options],
  );
  const optionGroups = useMemo(() => createOptionGroups(allOptions), [allOptions]);
  const checkoutStepSections = useMemo(() => createCheckoutStepSections(optionGroups), [optionGroups]);
  const archivedOptionIds = useMemo(() => archivedOptions.map((option) => option.id), [archivedOptions]);

  const formState = useFormState({
    config: formConfig,
    init: { input: props, map: (queryResults) => mapConfigToForm(queryResults) },
  });
  const [saveOfferingConfig] = useSaveProductOfferingConfigMutation();
  const [computeAppliedConfig, computeQuery] = useAddProductOfferingConfig_ComputeLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [computeAppliedProgramData] = useAddProductOfferingConfig_ProgramDataLazyQuery({ fetchPolicy: "no-cache" });

  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>(initialOptionIds);
  const [appliedOptionIds, setAppliedOptionIds] = useState<string[]>([]);
  const [appliedComputeResult, setAppliedComputeResult] = useState<AddProductOfferingConfig_ComputeResultFragment>();
  const [sellableSqft, setSellableSqft] = useState<number | undefined>(undefined);

  const [selectedOptionIdByGroupId, disabledOptionIds] = useMemo(
    () => [
      createSelectedOptionIdByGroupId(allOptions, selectedOptionIds),
      getDisabledOptionIds(allOptions, selectedOptionIds),
    ],
    [allOptions, selectedOptionIds],
  );
  const [appliedOptionCostById, totalCostInCents] = useMemo(() => {
    if (!appliedComputeResult) return [new Map<string, number>(), 0];
    return [createOptionCostById(appliedComputeResult), sumComputeResultCostInCents(appliedComputeResult)];
  }, [appliedComputeResult]);

  const totalCostSqFtInCents = useMemo(() => {
    if (!sellableSqft || !totalCostInCents) return 0;
    return Math.round(totalCostInCents / sellableSqft);
  }, [totalCostInCents, sellableSqft]);

  const selectedArchivedOptionIds = useMemo(
    () => selectedOptionIds.filter((optionId) => archivedOptionIds.includes(optionId)),
    [archivedOptionIds, selectedOptionIds],
  );
  const appliedArchivedOptionIds = useMemo(
    () => appliedOptionIds.filter((optionId) => archivedOptionIds.includes(optionId)),
    [appliedOptionIds, archivedOptionIds],
  );
  const showArchivedSection =
    archivedOptions.nonEmpty && (selectedArchivedOptionIds.nonEmpty || appliedArchivedOptionIds.nonEmpty);

  const loadOptionIds = useCallback(
    (optionIds: string[]) => {
      setSelectedOptionIds(optionIds);
      formState.readyPlanOptions.set(toFormReadyPlanOptions(optionIds));
    },
    [formState],
  );

  const updateAppliedConfig = useCallback(
    async (optionIds: string[], originalOptionIds?: string[]) => {
      // Two queries fired in parallel: the cost compute is versioned (CHECKOUT),
      // program data is unversioned. Bundling them into one request makes the
      // backend reject with "Cannot pin rp:X because it or its entities are
      // already in the EntityManager", so they must be separate requests.
      const [computeRes, programDataRes] = await Promise.all([
        computeAppliedConfig({
          variables: {
            readyPlanId: productOfferingId,
            readyPlanOptionIds: optionIds,
            originalReadyPlanOptionIds: originalOptionIds?.nonEmpty ? originalOptionIds : undefined,
          },
        }),
        computeAppliedProgramData({ variables: { readyPlanId: productOfferingId, readyPlanOptionIds: optionIds } }),
      ]);
      const result = computeRes.data?.computeReadyPlanCost;
      if (!result) return;

      // computeReadyPlanCost can omit archived options, but keep them selected until the user explicitly removes them.
      const submittedArchivedOptionIds = optionIds.filter((optionId) => archivedOptionIds.includes(optionId));
      const resolvedOptionIds = [
        ...result.readyPlanOptions.map((option) => option.id),
        ...submittedArchivedOptionIds,
      ].unique();
      loadOptionIds(resolvedOptionIds);
      setAppliedOptionIds(resolvedOptionIds);
      setAppliedComputeResult(result);
      const sqft = programDataRes.data?.computeProgramData?.sellableSqft;
      setSellableSqft(sqft ? Number(sqft) : undefined);
    },
    [archivedOptionIds, computeAppliedConfig, computeAppliedProgramData, loadOptionIds, productOfferingId],
  );

  // Hydrate once on mount; further compute calls happen via the explicit Update button.
  const didUpdateInitialConfig = useRef(false);
  useEffect(() => {
    if (didUpdateInitialConfig.current) return;
    didUpdateInitialConfig.current = true;
    void updateAppliedConfig(initialOptionIds);
  }, [initialOptionIds, updateAppliedConfig]);

  const handleOptionGroupSelect = useCallback(
    (optionGroupId: string, optionId: string | undefined) => {
      const nextOptionIds = updateSelectedOptionIdsForGroup(allOptions, selectedOptionIds, optionGroupId, optionId);
      loadOptionIds(nextOptionIds);
    },
    [allOptions, loadOptionIds, selectedOptionIds],
  );
  const handleArchivedOptionsSelect = useCallback(
    (nextArchivedOptionIds: string[]) => {
      const archivedOptionIdSet = new Set(archivedOptionIds);
      loadOptionIds([
        ...selectedOptionIds.filter((optionId) => !archivedOptionIdSet.has(optionId)),
        ...nextArchivedOptionIds,
      ]);
    },
    [archivedOptionIds, loadOptionIds, selectedOptionIds],
  );

  const hasUnappliedSelections = !selectedOptionIds.hasSameElements(appliedOptionIds);
  const configurationErrors = appliedComputeResult?.configurationErrors ?? [];
  const missingRequiredRpos = useMemo(
    () => getMissingRequiredRpos(optionGroups, appliedOptionIds),
    [appliedOptionIds, optionGroups],
  );
  const disableSave =
    (computeQuery.loading && "Updating selections") ||
    (!appliedComputeResult && "Update selections before saving") ||
    (hasUnappliedSelections && "Update selections before saving") ||
    (configurationErrors.nonEmpty && "Resolve configuration errors before saving") ||
    missingRequiredRpos;
  const disableUpdate =
    (computeQuery.loading && "Updating selections") ||
    (!!appliedComputeResult && !hasUnappliedSelections && "Selections are already up to date");

  const redirectRoute = useMemo(() => {
    if (configIds) {
      return createProductOfferingConfigsCompareUrl(JSON.parse(configIds), productOfferingId, devId, projectId);
    }
    if (devId || projectId) {
      return createProductOfferingConfigsUrl(devId, projectId, productOfferingId);
    }
    return "/";
  }, [devId, projectId, configIds, productOfferingId]);

  // Breadcrumb back to the page the user came from — Compare page when we have configIds,
  // configs list when we have a dev/project context. Otherwise we have no parent to link to.
  const parentBreadcrumb = useMemo(() => {
    if (configIds) return { label: "Compare Configurations", href: redirectRoute };
    if (devId || projectId) return { label: "Configurations", href: redirectRoute };
    return undefined;
  }, [configIds, devId, projectId, redirectRoute]);

  const canCreateProject = !!productOfferingConfig && (!!devId || !!projectId) && !isArchivedOffering;

  return (
    <FormPageLayout
      formSections={[
        {
          title: "Details",
          rows: [
            { name: boundTextField({ placeholder: "Configuration Name Goes Here" }) },
            {
              reactNodeSubtitle:
                "Select options, click Update to resolve the configuration, then Save the configuration to be pulled into underwriting.",
            },
            {
              reactNodeCostSummary: (
                <div css={Css.w50.df.fdr.jcsb.$}>
                  <StaticField label="Total Cost">
                    <Price valueInCents={totalCostInCents} />
                  </StaticField>
                  <StaticField label="Cost / Sq Ft">
                    <Price valueInCents={totalCostSqFtInCents} />
                  </StaticField>
                </div>
              ),
            },
            {
              isBaseConfig: boundCheckboxField({ label: "Set as base" }),
              publishToUnderwriting: boundCheckboxField({
                disabled: metaData.productOffering?.publishToUnderwriting
                  ? false
                  : "Disabled because the product offering is not published to underwriting",
              }),
            },
            {
              reactNodeErrors: (
                <div css={Css.df.fdc.gap2.$}>
                  {hasUnappliedSelections && appliedComputeResult && (
                    <span css={Css.sm.red600.$}>Selections have not been updated yet.</span>
                  )}
                  {configurationErrors.nonEmpty && (
                    <div css={Css.sm.red600.$} data-testid="configErrors">
                      {configurationErrors.length} {pluralize(configurationErrors.length, "Configuration Error")}:
                      <ul css={Css.pl2.$}>
                        {configurationErrors.map((error) => (
                          <li key={error}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ),
            },
          ],
        },
        ...checkoutStepSections.map((section) => ({
          title: section.title,
          rows: [
            {
              reactNode: (
                <div css={Css.df.fdc.gap2.$}>
                  {section.optionGroups.map((optionGroup) => (
                    <ReadyPlanOptionGroupSelect
                      key={optionGroup.id}
                      optionGroup={optionGroup}
                      value={selectedOptionIdByGroupId.get(optionGroup.id)}
                      selectedOptionIds={selectedOptionIds}
                      appliedOptionIds={appliedOptionIds}
                      appliedOptionCostById={appliedOptionCostById}
                      disabledOptionIds={disabledOptionIds}
                      onSelect={(optionId) => handleOptionGroupSelect(optionGroup.id, optionId)}
                    />
                  ))}
                </div>
              ),
            },
          ],
        })),
        ...(showArchivedSection
          ? [
              {
                title: "Archived",
                rows: [
                  {
                    reactNode: (
                      <MultiSelectField
                        data-testid="archivedOptions"
                        label="Archived Options"
                        values={selectedArchivedOptionIds}
                        options={archivedOptions}
                        getOptionValue={(option) => option.id}
                        getOptionLabel={(option) => getOptionLabel(option, appliedOptionIds, appliedOptionCostById)}
                        onSelect={handleArchivedOptionsSelect}
                        nothingSelectedText="None"
                        autoSort={false}
                      />
                    ),
                  },
                ],
              },
            ]
          : []),
      ]}
      formState={formState}
      pageTitle={`${isNew ? "New" : "Edit"} Configurations`}
      breadCrumb={[
        {
          label: metaData.productOffering.name,
          href: createProductOfferingUrl(
            metaData.productOffering.id,
            metaData.productOffering.version.id,
            devId,
            projectId,
          ),
        },
        ...(parentBreadcrumb ? [parentBreadcrumb] : []),
      ]}
      submitAction={{
        label: "Save",
        onClick: async () => {
          await saveOfferingConfig({ variables: { input: mapFormToInput(formState, appliedOptionIds) } });
          return navigate(redirectRoute);
        },
        disabled: disableSave,
      }}
      cancelAction={{
        label: computeQuery.loading ? "Updating..." : "Update",
        onClick: () => void updateAppliedConfig(selectedOptionIds, appliedOptionIds),
        disabled: disableUpdate,
      }}
      tertiaryAction={
        canCreateProject
          ? {
              label: "Create Project",
              onClick: () =>
                openModal({
                  content: (
                    <CreateProjectFromOfferingConfigModal
                      productOfferingConfig={productOfferingConfig!}
                      developmentId={devId}
                      projectId={projectId}
                    />
                  ),
                }),
            }
          : { label: "Cancel", onClick: redirectRoute }
      }
    />
  );
}

export type ProductOfferingConfigForm = Omit<SaveProductOfferingConfigInput, "readyPlanOptionIds"> & {
  readyPlanOptions: { readyPlanOptionId: string | null | undefined }[];
};

export const formConfig: ObjectConfig<ProductOfferingConfigForm> = {
  id: { type: "value" },
  productOfferingId: { type: "value", rules: [required] },
  name: { type: "value", rules: [required] },
  readyPlanOptions: { type: "list", config: { readyPlanOptionId: { type: "value" } } },
  isBaseConfig: { type: "value" },
  publishToUnderwriting: { type: "value" },
};

export function mapConfigToForm(queryResults: AddOfferingConfigPageProps): ProductOfferingConfigForm {
  const { productOfferingConfig, metaData } = queryResults;
  const savedOptionIds = productOfferingConfig?.readyPlanOptions.filter((rpo) => rpo.active).map((rpo) => rpo.id) ?? [];
  return {
    ...productOfferingConfig,
    productOfferingId: metaData.productOffering.id,
    readyPlanOptions: toFormReadyPlanOptions(savedOptionIds),
  };
}

export function mapFormToInput(
  formState: ObjectState<ProductOfferingConfigForm>,
  appliedOptionIds: string[],
): SaveProductOfferingConfigInput {
  const { readyPlanOptions: _ignored, ...rest } = formState.value;
  return { ...rest, readyPlanOptionIds: appliedOptionIds };
}
